// src/pages/ResetPasswordPage.js
import React, { useEffect, useState, useContext } from "react";
import {
  Container,
  Paper,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Grid,
  useTheme,
  useMediaQuery,
  InputAdornment,
  IconButton,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  CheckCircleOutline,
  ErrorOutline,
} from "@mui/icons-material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useSearchParams, useNavigate } from "react-router-dom";
import { AuthContext } from "../../contexts/AuthContext";

const PASSWORD_REGEX =
  /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/;

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const email = searchParams.get("email");
  const [statusMsg, setStatusMsg] = useState({ type: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [tokenValid, setTokenValid] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { resetPassword } = useContext(AuthContext);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    if (!token || !email) {
      setTokenValid(false);
    }
  }, [token, email]);

  const formik = useFormik({
    initialValues: {
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      newPassword: Yup.string()
        .matches(
          PASSWORD_REGEX,
          "Must contain 8+ characters, 1 letter, 1 number, and 1 special character"
        )
        .required("New password is required"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
        .required("Please confirm your password"),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      setStatusMsg({ type: "", message: "" });

      try {
        const { newPassword } = values;

        await resetPassword({ token, newPassword });

        setStatusMsg({
          type: "success",
          message: "Password reset successfully. Redirecting to login page...",
        });

        setRedirecting(true);

        setTimeout(() => {
          navigate("/auth?request_type=login", { replace: true });
        }, 1500);
      } catch (error) {
        setStatusMsg({
          type: "error",
          message:
            error?.response?.data?.message || "Failed to update password.",
        });
      } finally {
        setLoading(false);
      }
    },
  });

  const passwordsMatch =
    formik.values.newPassword &&
    formik.values.confirmPassword &&
    formik.values.newPassword === formik.values.confirmPassword;

  if (!tokenValid) {
    return (
      <Container maxWidth='sm'>
        <Paper
          elevation={3}
          sx={{
            padding: theme.spacing(4),
            marginTop: theme.spacing(8),
            borderRadius: 3,
          }}
        >
          <Alert severity='error'>
            This reset link has expired or is invalid.
          </Alert>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth='sm'>
      <Paper
        elevation={3}
        sx={{
          padding: theme.spacing(isSmallScreen ? 3 : 4),
          width: "100%",
          mt: isSmallScreen ? 2 : 6,
          borderRadius: 3,
          boxShadow:
            "0px 10px 25px rgba(0, 0, 0, 0.08), 0px 2px 8px rgba(0, 0, 0, 0.04)",
        }}
      >
        <Box mb={3}>
          <Typography
            variant='h5'
            component='h1'
            gutterBottom
            sx={{ fontWeight: 600 }}
          >
            Reset Your Password
          </Typography>
          <Typography variant='body2' color='textSecondary'>
            You're resetting the password for <strong>{email}</strong>
          </Typography>
        </Box>

        {statusMsg.message && (
          <Alert severity={statusMsg.type} sx={{ mb: 2 }}>
            {statusMsg.message}
          </Alert>
        )}

        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label='New Password'
                name='newPassword'
                type={showPassword ? "text" : "password"}
                value={formik.values.newPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.newPassword &&
                  Boolean(formik.errors.newPassword)
                }
                helperText={
                  formik.touched.newPassword && formik.errors.newPassword
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton
                        aria-label='toggle password visibility'
                        onClick={() => setShowPassword((prev) => !prev)}
                        edge='end'
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Box mt={1} sx={{ fontSize: 12, color: "text.secondary" }}>
                Must be at least 8 characters and include a letter, number, and
                special character.
              </Box>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label='Confirm New Password'
                name='confirmPassword'
                type={showConfirmPassword ? "text" : "password"}
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.confirmPassword &&
                  (Boolean(formik.errors.confirmPassword) || !passwordsMatch)
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton
                        aria-label='toggle confirm password visibility'
                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                        edge='end'
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {formik.values.confirmPassword && (
                <Box
                  mt={1}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    fontSize: 12,
                    color: passwordsMatch ? "success.main" : "error.main",
                  }}
                >
                  {passwordsMatch ? (
                    <CheckCircleOutline
                      sx={{ fontSize: 16, mr: 0.5 }}
                      color='success'
                    />
                  ) : (
                    <ErrorOutline
                      sx={{ fontSize: 16, mr: 0.5 }}
                      color='error'
                    />
                  )}
                  {passwordsMatch
                    ? "Passwords match"
                    : "Passwords do not match"}
                </Box>
              )}
            </Grid>

            <Grid item xs={12}>
              <Button
                type='submit'
                variant='contained'
                color='primary'
                fullWidth
                sx={{ mt: 2, height: 45, position: "relative" }}
                disabled={loading || redirecting}
              >
                {loading ? (
                  <CircularProgress size={24} sx={{ color: "white" }} />
                ) : (
                  "Reset Password"
                )}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Container>
  );
};

export default ResetPasswordPage;
